import {
  IPHONE_17_PRO_CORNER_RADIUS,
  IPHONE_17_PRO_VIEWPORT_HEIGHT,
  IPHONE_17_PRO_VIEWPORT_WIDTH,
} from './device.js';

/**
 * The default bedroom — milestone 1's only sleeper/base.
 *
 * Per docs/PLAN.md §4.1 (Sleeper) the surface is a 1D heightfield of
 * segments with per-segment stiffness, plus a verlet blanket. For
 * milestone 1 we approximate with a simple polyline that has soft
 * (lower stiffness) and firm (higher stiffness) regions.
 *
 * Coordinates are world units. The bed sits at the bottom of the
 * playfield; the stack rises upward. World coordinates use y-up,
 * which is what the renderer translates to canvas y-down at draw time.
 */

export interface SurfaceSegment {
  /** Center x of this segment (world units). */
  x: number;
  /** Surface y at this segment (world units, y-up). */
  y: number;
  /**
   * Stiffness coefficient (0..1). Higher = firmer; lower = softer.
   * Milestone 1 uses this only for visual deformation rendering;
   * full verlet physics arrives in milestone 2.
   */
  stiffness: number;
}

export interface BaseDef {
  id: string;
  displayName: string;
  /** Width of the bed in world units. */
  bedWidth: number;
  /** Height of the bed (the headroom above the bed surface for stacking). */
  playfieldHeight: number;
  /** Rounded playfield corner radius (world units, matches target device). */
  cornerRadius: number;
  /** Heightfield segments left-to-right across the bed. */
  segments: readonly SurfaceSegment[];
  /** Background fill color (greybox). */
  bgColor: number;
  /** Sleeper body fill color (greybox). */
  sleeperColor: number;
  /** Blanket / surface fill color (greybox). */
  blanketColor: number;
}

/** Build a smooth heightfield with a belly (soft) and shins (firm). */
function buildBedroomSegments(width: number, surfaceY: number): SurfaceSegment[] {
  const segments: SurfaceSegment[] = [];
  const count = 24;
  for (let i = 0; i < count; i++) {
    const x = (i / (count - 1)) * width;
    // The center of the bed is the belly: lower stiffness, slight dip.
    // The right side (~70-90%) is the shins: higher stiffness, gentle hump.
    const t = i / (count - 1);
    let y = surfaceY;
    let stiffness = 0.7;

    if (t > 0.35 && t < 0.55) {
      // Belly dip
      y -= 4;
      stiffness = 0.45;
    } else if (t > 0.7 && t < 0.9) {
      // Shins rise
      y += 6;
      stiffness = 0.85;
    } else if (t > 0.05 && t < 0.2) {
      // Chest
      y += 3;
      stiffness = 0.75;
    }
    segments.push({ x, y, stiffness });
  }
  return segments;
}

const BED_WIDTH = IPHONE_17_PRO_VIEWPORT_WIDTH;
const PLAYFIELD_HEIGHT = IPHONE_17_PRO_VIEWPORT_HEIGHT;
const SURFACE_Y = 100;

/** Y of the sleeping surface (blanket line), ¼ up from the playfield bottom. */
export function bedTopY(playfieldHeight: number = PLAYFIELD_HEIGHT): number {
  return (playfieldHeight * 3) / 4;
}

/** Y of the foot of the bed, ⅙ up from the playfield bottom. */
export function bedBottomY(playfieldHeight: number = PLAYFIELD_HEIGHT): number {
  return (playfieldHeight * 5) / 6;
}

/** Vertical extent of the bed body in world units. */
export function bedDepth(playfieldHeight: number = PLAYFIELD_HEIGHT): number {
  return bedBottomY(playfieldHeight) - bedTopY(playfieldHeight);
}

export const BEDROOM_DEFAULT: BaseDef = {
  id: 'bedroom_default',
  displayName: 'Bedroom (default)',
  bedWidth: BED_WIDTH,
  playfieldHeight: PLAYFIELD_HEIGHT,
  cornerRadius: IPHONE_17_PRO_CORNER_RADIUS,
  segments: buildBedroomSegments(BED_WIDTH, SURFACE_Y),
  bgColor: 0xffffff,
  sleeperColor: 0x3a3344,
  blanketColor: 0x6a5a82,
};

/** The bed surface y for the highest cat measurement (height from here). */
export const BEDROOM_SURFACE_Y = SURFACE_Y;
